import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { axiosInstance } from "../App";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function TaskChecklist({ taskId, checklist, onUpdate }) {
  const [newItemText, setNewItemText] = useState("");
  const [adding, setAdding] = useState(false);
  const [localChecklist, setLocalChecklist] = useState(checklist || []);

  // Update local checklist when prop changes
  useEffect(() => {
    setLocalChecklist(checklist || []);
  }, [checklist]);

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemText.trim()) return;

    setAdding(true);
    try {
      await axiosInstance.post(`/tasks/${taskId}/checklist`, {
        text: newItemText
      });
      setNewItemText("");
      toast.success("Öğe eklendi");
      onUpdate();
    } catch (error) {
      toast.error("Öğe eklenemedi");
    } finally {
      setAdding(false);
    }
  };

  const handleToggleItem = async (itemId, currentStatus) => {
    // Optimistic update
    setLocalChecklist(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, is_completed: !currentStatus }
          : item
      )
    );

    try {
      await axiosInstance.put(`/tasks/${taskId}/checklist/${itemId}`, {
        is_completed: !currentStatus
      });
      onUpdate();
    } catch (error) {
      toast.error("Durum güncellenemedi");
      console.error("Checklist toggle error:", error);
      // Revert on error
      setLocalChecklist(checklist || []);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axiosInstance.delete(`/tasks/${taskId}/checklist/${itemId}`);
      toast.success("Öğe silindi");
      onUpdate();
    } catch (error) {
      toast.error("Öğe silinemedi");
    }
  };

  const completedCount = localChecklist.filter(item => item.is_completed).length;
  const progress = localChecklist.length > 0 ? Math.round((completedCount / localChecklist.length) * 100) : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold">Checklist</h4>
        {localChecklist.length > 0 && (
          <span className="text-sm text-gray-500">
            {completedCount}/{localChecklist.length} ({progress}%)
          </span>
        )}
      </div>

      {localChecklist.length > 0 && (
        <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
          <div
            className="bg-green-500 h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}

      <div className="space-y-2">
        {localChecklist.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 group"
            data-testid={`checklist-item-${item.id}`}
          >
            <Checkbox
              checked={item.is_completed}
              onCheckedChange={() => handleToggleItem(item.id, item.is_completed)}
              data-testid={`checklist-checkbox-${item.id}`}
            />
            <span
              className={`flex-1 text-sm ${
                item.is_completed ? "line-through text-gray-400" : ""
              }`}
            >
              {item.text}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100"
              onClick={() => handleDeleteItem(item.id)}
            >
              <Trash2 className="h-3 w-3 text-red-500" />
            </Button>
          </div>
        ))}
      </div>

      <form onSubmit={handleAddItem} className="flex gap-2">
        <Input
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          placeholder="Yeni öğe ekle..."
          className="flex-1"
          data-testid="checklist-input"
        />
        <Button
          type="submit"
          size="sm"
          disabled={adding || !newItemText.trim()}
          data-testid="add-checklist-item"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

export default TaskChecklist;
